// @flow
/* eslint-disable ta-code-conventions/has-matching-storydoc,ta-code-conventions/has-matching-test */
// See useVDOMPortal.story.jsx
import * as React from 'react';
import generateUID from './uid';

type PortaledElement = {|
  node: React.Node,
  /** Denote identity of an overlay element in respect to the internal store */
  id: string | number,
  /** Denote identity of a React element in respect to React */
  key?: string,
  stateChange: boolean => mixed,
|};

type PortalStore = PortaledElement[];

type Props = {|
  children: React.Node,
|};

/** Available options for opening an overlay */
type MountElementOptions = {|
  /** If true, then opening an overlay that is already open will not push the element on top of other overlays. It will stay in place */
  upsert: boolean,
|};

type MountElement = (overlay: PortaledElement, options?: MountElementOptions) => mixed;
type UnmountElement = (id: string | number) => mixed;
type VDOMContext = {|
  mountNode: MountElement,
  unmountNode: UnmountElement,
|};

const Selector = ({ children }) => children;
// Used so whenever the state changes, the children won't re-render
// This is just an optimization so it does not re-render near the top of the tree; the component actually using the context will still re-render
const PureSelector = React.memo<_>(Selector);

/** The VirtualDOMPortal context */
export const VirtualDOMPortal = React.createContext<VDOMContext>({
  mountNode: () => {},
  unmountNode: () => {},
});

if (DEBUG) {
  VirtualDOMPortal.displayName = 'VirtualDOMPortal';
}

/** Reference to the internal portal store, used only for debugging purposes. Do not import or use this value in development! */
// eslint-disable-next-line no-underscore-dangle
export const __DEBUG_STORE__ = { current: [] };

const VirtualDOMPortalProvider = ({ children }: Props) => {
  const [store, setStore] = React.useState<PortalStore>([]);
  const { current: registry } = React.useRef([]);
  const context = React.useMemo<VDOMContext>(
    () => ({
      mountNode: (openedElement, options = { upsert: false }) => {
        const key = generateUID();
        if (!options.upsert) {
          setStore(currentElements => [
            ...currentElements.filter(element => openedElement.id !== element.id),
            { ...openedElement, key },
          ]);
        } else {
          setStore(currentElements => {
            const index = currentElements.findIndex(element => element.id === openedElement.id);
            // upserts always persist keySuffix property so elements do not unmount and remount when it already exists
            const upsertedElement = {
              ...openedElement,
              key: index === -1 ? key : currentElements[index].key,
            };
            currentElements.splice(index, index === -1 ? 0 : 1, upsertedElement);
            return [...currentElements];
          });
        }
        registry.push({ id: openedElement.id, stateChange: openedElement.stateChange });
        openedElement.stateChange(true);
      },
      unmountNode: id => {
        setStore(currentElements => currentElements.filter(element => element.id !== id));
        // Call stateChange on all elements that have closed (corresponding to the indices)
        const indices = registry
          .map((element, index) => (element.id === id ? index : null))
          .filter(index => index !== null);
        indices
          .map(index => registry.splice((index: any), 1))
          .forEach(spliced => {
            spliced.forEach(({ stateChange }) => {
              stateChange(false);
            });
          });
      },
    }),
    [registry],
  );

  // DEBUG is an immutable value, so the order of hook execution is the same
  // By wrapping in a DEBUG, this code can be stripped out in production
  if (DEBUG) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    React.useEffect(() => {
      __DEBUG_STORE__.current = store;
    });
  }

  return (
    <VirtualDOMPortal.Provider value={context}>
      <PureSelector>{children}</PureSelector>
      {store.map(({ node, key, id }) => (
        <PureSelector key={`VDOMPortal:${key || id}`}>{node}</PureSelector>
      ))}
    </VirtualDOMPortal.Provider>
  );
};

export default VirtualDOMPortalProvider;
